// src/components/bookings_manager/schedule_planner/content/ScheduleAssignmentDrawerContent/components/UserVisibleScheduleRemarksSection.js
import styled from "styled-components";
import { useState } from "react";
import moment from "moment";
import { useFetchEntities } from "@/reactQueryHooks";
import {
    SectionContainer,
    SectionTitle,
    EmptyMessage,
    colors,
} from "../styles";

const RemarksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RemarksCard = styled.div`
  display: flex;
  flex-direction: column;
  padding: 14px 16px;
  border-radius: 8px;
  background: rgba(56, 189, 248, 0.12);
  border-left: 3px solid ${colors.info};
  transition: all 0.2s ease;

  &:hover {
    background: rgba(56, 189, 248, 0.18);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  }
`;

const RemarksHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const RemarksMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RemarksMetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;

  i {
    font-size: 10px;
  }
`;

const RemarksText = styled.span`
  font-size: 13px;
  color: ${colors.textPrimary};
  line-height: 1.4;
`;

const RemarksActions = styled.div`
  display: flex;
  gap: 8px;
  opacity: 0.2;
  transition: opacity 0.2s ease;

  ${RemarksCard}:hover & {
    opacity: 1;
  }
`;

const RemarksActionButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => (props.delete ? colors.danger : colors.info)};
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
        props.delete ? "rgba(247, 37, 133, 0.1)" : "rgba(56, 189, 248, 0.15)"};
  }
`;

const RemarksInputContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 5px;
`;

const RemarksInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 1px solid ${colors.border};
  border-radius: 10px;
  outline: none;
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    border-color: ${colors.info};
    box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.2);
  }

  &::placeholder {
    color: ${colors.gray};
  }
`;

const RemarksSaveButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 46px;
  height: 46px;
  border-radius: 10px;
  border: none;
  background: ${(props) => (props.edit ? colors.info : colors.primary)};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.edit ? "#0EA5E9" : colors.secondary)};
  }

  i {
    font-size: 16px;
  }
`;

const UserVisibleScheduleRemarksSection = ({
    scheduleData,
    onSaveRemarks,
    onDeleteRemarks,
}) => {
    const [remarkInput, setRemarkInput] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const { data: entities } = useFetchEntities();

    const hasRemarks = Boolean(scheduleData?.userVisibleScheduleRemarks);
    const remark = scheduleData?.userVisibleScheduleRemarks;

    const remarkAuthor = hasRemarks
        ? entities?.data?.staff?.find((user) => user._id === remark?.author_id)
        : null;

    const remarkDate = hasRemarks
        ? moment(remark?.date).format("DD/MM/YYYY HH:mm:ss")
        : null;

    const handleSaveRemarks = () => {
        if (!remarkInput.trim()) return;

        onSaveRemarks(remarkInput);
        setRemarkInput("");
        setIsEditing(false);
    };

    const handleStartEditing = () => {
        setRemarkInput(remark?.remarkBody || "");
        setIsEditing(true);
    };

    const handleCancelEditing = () => {
        setRemarkInput("");
        setIsEditing(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSaveRemarks();
        } else if (e.key === "Escape") {
            handleCancelEditing();
        }
    };

    return (
        <SectionContainer>
            <SectionTitle>
                <i className="fa-solid fa-comments"></i>
                User Visible Remarks
            </SectionTitle>

            {isEditing || !hasRemarks ? (
                <RemarksInputContainer>
                    <RemarksInput
                        placeholder="Add a remark that users can see..."
                        value={remarkInput}
                        onChange={(e) => setRemarkInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        autoFocus
                    />
                    <RemarksSaveButton onClick={handleSaveRemarks} edit={isEditing}>
                        <i className={`fa-solid ${isEditing ? "fa-check" : "fa-plus"}`}></i>
                    </RemarksSaveButton>
                    {isEditing && (
                        <RemarksSaveButton
                            onClick={handleCancelEditing}
                            edit={false}
                            style={{ background: colors.gray }}
                        >
                            <i className="fa-solid fa-times"></i>
                        </RemarksSaveButton>
                    )}
                </RemarksInputContainer>
            ) : (
                <RemarksContainer>
                    <RemarksCard>
                        <RemarksHeader>
                            <RemarksMeta>
                                <RemarksMetaItem>
                                    <i className="fa-solid fa-user"></i>
                                    <span>{remarkAuthor?.name || "Unknown User"}</span>
                                </RemarksMetaItem>
                                <RemarksMetaItem>
                                    <i className="fa-solid fa-clock"></i>
                                    <span>{remarkDate}</span>
                                </RemarksMetaItem>
                            </RemarksMeta>
                            <RemarksActions>
                                <RemarksActionButton onClick={handleStartEditing}>
                                    <i className="fa-solid fa-edit"></i>
                                </RemarksActionButton>
                                <RemarksActionButton
                                    delete
                                    onClick={() => {
                                        if (window.confirm("Delete this remark?")) {
                                            onDeleteRemarks();
                                        }
                                    }}
                                >
                                    <i className="fa-solid fa-trash"></i>
                                </RemarksActionButton>
                            </RemarksActions>
                        </RemarksHeader>
                        <RemarksText>{remark?.remarkBody}</RemarksText>
                    </RemarksCard>
                </RemarksContainer>
            )}

            {!hasRemarks && !isEditing && (
                <EmptyMessage
                    style={{
                        background: "rgba(56, 189, 248, 0.08)",
                        color: colors.info,
                    }}
                >
                    No user visible remarks added
                </EmptyMessage>
            )}
        </SectionContainer>
    );
};

export default UserVisibleScheduleRemarksSection;

